import { BusinessDayCalculatorPL, CompanyParametersQuery, ContextKeys, DocumentExchangeRateValidationBoPart, DocumentItemsNumberingBoPart, DocumentNumberBoPart, ExchangeRateMixin, InventoryQuery, NumberingQuery, PurchaseInvoiceRepositoryContract, rewriteDocumentContractor, rewriteDocumentItem, rewriteExchangeRate, VatRateForPurchaseQuery } from '@skylab-shared/business';
import { BO, BusinessObjectBase, QueryBus, Weaver } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, InventoryDocumentItemModel, InventoryDocumentKind, InventoryGoodsReceiptDocumentModel, InventoryRelationshipCreationMethod, ProductModel, RelatedEntity } from '@skylab-shared/dataModel';
import { head, not, substractDays } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { InventoryDocumentCalculatorBoPart } from './bo-parts/inventory-document-calculator.bo-part';
import { InventoryDocumentItemsValidationsBoPart } from './bo-parts/inventory-document-items-validations.bo-part';
import { InventoryLinksMixin } from './mixins/inventory-links.mixin';
import { InventoryDocumentAssociatedWithGoodsIssueQuery } from './queries/inventory-document-associated-with-goods-issue.query';
import { refreshInventoryReceiptDocumentItemPriceAsync } from './utils/inventory-document-item-utils';
import { InventoryGoodsReceiptItemCannotBeOneTimeUsage } from './violations/inventory-goods-receipt-item-cannot-be-one-time-usage-error';

export interface InventoryGoodsReceiptDocumentBO extends InventoryLinksMixin<InventoryGoodsReceiptDocumentModel>, ExchangeRateMixin<InventoryGoodsReceiptDocumentModel> {};

@BO({
    modelType: InventoryGoodsReceiptDocumentModel,
    mixins: [InventoryLinksMixin, ExchangeRateMixin]
})
export class InventoryGoodsReceiptDocumentBO extends BusinessObjectBase<InventoryGoodsReceiptDocumentModel> {
    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepository,
        protected readonly queryBus: QueryBus,
        protected readonly purchaseInvoiceRepository: PurchaseInvoiceRepositoryContract
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(InventoryQuery),
            this.localContext.preload(CompanyParametersQuery)
        ]);

        if (this.model.isPersistent) {
            await this.loadLinksAsync();
            this.internalIsDocumentAssociatedWithPurchaseDocument = !!this.linksCommercial;
        }

        await this.setDocumentAssociatedWithGoodsIssueAsync();

        this.registerDocumentReadonlyRule();
        this.registerItemRules();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new InventoryDocumentCalculatorBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new InventoryDocumentItemsValidationsBoPart(this.weaver),
            new DocumentExchangeRateValidationBoPart(this.weaver)
        ]);
    }

    private registerItemRules(): void {
        this.validation.addRule(
            [[InventoryDocumentItemModel, 'productName'], [InventoryDocumentItemModel, 'productId']],
            [[InventoryDocumentItemModel, 'productName']],
            ctx => not(this.isItemOneTimeUsage(ctx.source) && !!ctx.target.productName),
            InventoryGoodsReceiptItemCannotBeOneTimeUsage
        );

        this.readonly.addRule(
            [[InventoryDocumentItemModel, 'productId']],
            [[InventoryDocumentItemModel, 'productName'], [InventoryDocumentItemModel, 'unitId']],
            ctx => not(this.isItemOneTimeUsage(ctx.source))
        );
    }

    private registerDocumentReadonlyRule(): void {
        const modelType = InventoryGoodsReceiptDocumentModel;

        this.readonly.addRule([
            [modelType, 'calculationMethod'],
            [modelType, 'costCalculationMethod'],
            [modelType, 'currencyId'],
            [modelType, 'documentNumber'],
            [modelType, 'inventoryId'],
            [modelType, 'issueDate'],
            [modelType, 'issuer'],
            [modelType, 'numberingId'],
            [modelType, 'receiptDate'],
            [modelType, 'recipient'],
            [modelType, 'seller'],
            [modelType, 'transactionLocation'],
        ], () => this.isAssociatedDocument);

        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => this.isAssociatedDocument);
    }

    public get isAssociatedDocument(): boolean {
        return this.isDocumentAssociatedWithGoodsIssue || this.isDocumentAdjusted || this.isDocumentAssociatedWithPurchaseDocument || this.isDocumentAssociatedWithStocktaking;
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.receiptDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => {
            const product = item.productId ? await this.localContext.getAsync(ContextKeys.product(item.productId), () => this.productRepository.findAsync(item.productId!)) : new ProductModel();
            await refreshInventoryReceiptDocumentItemPriceAsync(item, this.localContext, this.model, product);
        }));
    }

    public isDocumentAssociatedWithGoodsIssue: boolean;
    private async setDocumentAssociatedWithGoodsIssueAsync(): Promise<void> {
        this.isDocumentAssociatedWithGoodsIssue = this.model.isPersistent
            ? await this.queryBus.queryAsync(new InventoryDocumentAssociatedWithGoodsIssueQuery(this.model.id))
            : false;
    }

    private isItemOneTimeUsage(item: InventoryDocumentItemModel): boolean {
        return !item.productId;
    }

    private internalIsDocumentAssociatedWithPurchaseDocument: boolean;
    public get isDocumentAssociatedWithPurchaseDocument() {
        return this.internalIsDocumentAssociatedWithPurchaseDocument;
    };

    public async fillGoodsReceiptToInvoiceAsync(purchaseDocumentId: number, creationMethod: InventoryRelationshipCreationMethod): Promise<void> {
        const source = await this.purchaseInvoiceRepository.findAsync(purchaseDocumentId);

        this.internalIsDocumentAssociatedWithPurchaseDocument = true;

        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery);
        const numberings = this.localContext.getPreloaded(NumberingQuery);
        const inventory = head(this.localContext.getPreloaded(InventoryQuery));
        const vatRates = this.localContext.getPreloaded(VatRateForPurchaseQuery);

        this.model.kind = InventoryDocumentKind.GoodsReceipt;
        this.model.issuedByVatPayer = companyParameters.vatPayer;
        this.model.inventoryId = inventory.id;
        this.model.relatedCommercialDocuments = [RelatedEntity.from(source)];

        rewriteDocumentContractor(this.model, source);

        this.model.calculationMethod = source.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net;
        this.model.costCalculationMethod = source.costCalculationMethod;
        this.model.currencyId = source.currencyId;

        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.includes(this.model.kind));
        this.model.numberingId = defaultNumberingId;

        this.model.receiptDate = source.deliveryDate;
        this.model.issueDate = source.deliveryDate;

        this.model.transactionLocation = source.transactionLocation;
        this.model.recipient = source.recipient;
        this.model.issuer = source.issuer;

        this.model.creationMethod = creationMethod;

        source.items.filter(elem => elem.includesInventoryControl).map(item => {
            const model = new InventoryDocumentItemModel();
            this.model.items.push(model);
            rewriteDocumentItem(model, item);
            model.vatRateId = item.vatRateId;
            model.relatedCommercialItemsIds = item.id ? [item.id] : [];
        });

        if (source.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, source.exchangeRate);
        }
    }
}
